"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { Sheet, SheetContent, SheetTitle } from "./sheet";

export interface SideOverlayDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  headerAction?: React.ReactNode;
  onBack?: () => void;
  className?: string;
  children: React.ReactNode;
}

export function SideOverlayDrawer({
  isOpen,
  onClose,
  title,
  headerAction,
  onBack,
  className,
  children,
}: SideOverlayDrawerProps) {
  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  const fallbackTitle = "Drawer panel";

  const classes = className ? className.split(" ") : [];
  const widthClasses = classes.filter(
    (c) =>
      c.startsWith("max-w-") ||
      c.startsWith("w-") ||
      c.startsWith("sm:max-w-") ||
      c.startsWith("md:max-w-") ||
      c.startsWith("lg:max-w-") ||
      c.startsWith("xl:max-w-")
  );
  const otherClasses = classes.filter((c) => !widthClasses.includes(c));

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          "border-none bg-transparent p-0 shadow-none",
          widthClasses.length > 0 ? widthClasses.join(" ") : "w-full max-w-[560px] sm:max-w-[680px]"
        )}
        aria-label={title ?? fallbackTitle}
      >
        <SheetTitle className="sr-only">{title ?? fallbackTitle}</SheetTitle>
        <div
          className={cn(
            "ml-auto flex h-dvh w-full overflow-y-auto overflow-x-hidden flex-col bg-[#FFF9EF] px-4 pb-6 pt-5",
            otherClasses.join(" ")
          )}
        >
          {(title || onBack || headerAction) && (
            <div className="mb-5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    aria-label="Go back"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-[#344054]"
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.625 3.75L6.375 9L11.625 14.25"
                        stroke="#344054"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
                {title && (
                  <h2 className="text-lg font-semibold text-[#344054]">
                    {title}
                  </h2>
                )}
              </div>
              {headerAction}
            </div>
          )}
          <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden">{children}</div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
